import {
  DailyCheckInCardState,
  DailyCheckInDay,
  DailyCheckInRewardStatus
} from '@apeiron/reward'
import { NextImage, useMessageContext } from '@apeiron/temp'
import { Box, Button, styled, Typography } from '@mui/material'
import ExclamationSVG from '@public/icons/icon-exclamation.svg'
import CheckInCardPNG from '@public/images/image-daily-checkin-card.png'
import CheckInCardOverlayPNG from '@public/images/image-daily-checkin-card-overlay.png'
import ApeironTick from '@src/components/share/apeiron/ApeironTick'
import { useDailyCheckInContext } from '@src/contexts/reward/DailyCheckInContext'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const CardButton = styled(Button)`
  aspect-ratio: 0.461;
  display: flex;
  justify-content: center;
  position: relative;
  width: 116px;
  text-transform: none;
`

const CardImage = styled(NextImage)`
  height: 100%;
  position: absolute;
  width: 100%;
`

const BlueOverlay = styled(CardImage)`
  filter: brightness(0) saturate(100%) invert(37%) sepia(73%) saturate(2170%)
    hue-rotate(214deg) brightness(100%) contrast(102%);
`

const DayLabel = styled(Typography)`
  position: absolute;
  font-size: 14px;
  color: black;
  top: 13%;
`

const QuantityLabel = styled(Box)`
  background-color: black;
  color: #f1b535;
  position: absolute;
  bottom: 26%;
  width: 55%;
  padding: 3px 20px;
  border-radius: 20px;
  font-size: 16px;
`

const ClaimCheckText = styled(Typography)`
  position: absolute;
  font-size: 16px;
  bottom: 3.5%;
  font-weight: 400;
`

const BlackClaimCheckText = styled(ClaimCheckText)`
  color: black;
`

const RedDot = styled(Box)`
  animation: bouncing 1.25s infinite;
  position: absolute;
  top: -2%;
  right: -5%;
  background-color: red;
  width: 15px;
  aspect-ratio: 1;
  border-radius: 50%;
`

const DarkOverlay = styled(Box)`
  background-color: #000000b4;
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
`

const Tick = styled(ApeironTick)`
  width: 50%;
  .Custom-Tick {
    filter: brightness(0) saturate(100%) invert(82%) sepia(33%) saturate(2974%)
      hue-rotate(61deg) brightness(105%) contrast(100%); // white to green
  }
`

const IconImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 50%;
`

const ClaimedText = styled(Typography)`
  font-size: 16px;
  color: #3aff37;
`

const ExclamationText = styled(ClaimedText)`
  color: #ff6937;
`

const ReclaimBox = styled(Box)`
  background-color: transparent;
  border: 2px solid #ff6937;
  color: #ff6937;
  position: absolute;
  border-radius: 40px;
  font-size: 12px;
  padding: 4px 12px;
  top: 16%;
`

const CheckInDayCard: FC<Props> = (props: Props) => {
  const { dayInfo, campaignId, className } = props

  const { t } = useTranslation()

  const {
    checkInOperation,
    claimDailyCheckIn,
    fetchAfterOperation,
    setShowMakeupPopup
  } = useDailyCheckInContext()

  const { setInfo, setSnackInfo } = useMessageContext()

  const cardState = useMemo(() => {
    if (dayInfo.status === DailyCheckInRewardStatus.Claimed) {
      return DailyCheckInCardState.claimed
    }

    if (dayInfo.status === DailyCheckInRewardStatus.Claimable) {
      return DailyCheckInCardState.claimable
    }

    if (dayInfo.status === DailyCheckInRewardStatus.Locked) {
      if (dayInfo.isCurrent) {
        return DailyCheckInCardState.checkIn
      } else if (!dayInfo.isCurrent && !dayInfo.isPast) {
        return DailyCheckInCardState.normal
      } else return DailyCheckInCardState.missed
    }

    return DailyCheckInCardState.normal
  }, [dayInfo])

  const renderOverlay = useCallback(
    (state: DailyCheckInCardState) => {
      switch (state) {
        case DailyCheckInCardState.missed:
          return (
            <DarkOverlay>
              <IconImage src={ExclamationSVG} />
              <ExclamationText>
                {t('adeptus.reward.checkin.ineligible')}
              </ExclamationText>
              <ReclaimBox>{t('adeptus.reward.checkin.reclaim')}</ReclaimBox>
            </DarkOverlay>
          )
        case DailyCheckInCardState.claimable:
          return (
            <>
              <CardImage src={CheckInCardOverlayPNG.src} />
              <BlackClaimCheckText>
                {t('adeptus.reward.checkin.claim_me')}
              </BlackClaimCheckText>
              <RedDot />
            </>
          )
        case DailyCheckInCardState.checkIn:
          return (
            <>
              <BlueOverlay src={CheckInCardOverlayPNG.src} />
              <ClaimCheckText>
                {t('adeptus.reward.checkin.checkin')}
              </ClaimCheckText>
              <RedDot />
            </>
          )
        case DailyCheckInCardState.claimed:
          return (
            <DarkOverlay>
              <Tick />
              <ClaimedText>{t('adeptus.reward.checkin.claimed')}</ClaimedText>
            </DarkOverlay>
          )
        default:
          return null
      }
    },
    [t]
  )

  const handleOnCheckInConfirm = useCallback(async () => {
    if (R.isNotNil(campaignId)) {
      setInfo(null)
      await checkInOperation(campaignId)
      await fetchAfterOperation()
    }
  }, [campaignId, checkInOperation, fetchAfterOperation, setInfo])

  const handleOnClaimConfirm = useCallback(async () => {
    if (R.isNotNil(campaignId)) {
      setInfo({
        message: t('reward.checkin.day_claiming')
      })

      await claimDailyCheckIn([
        { campaignID: campaignId, day: dayInfo.day, cycle: dayInfo.cycle }
      ])

      await fetchAfterOperation()

      setInfo(null)

      setSnackInfo({
        message: t('reward.checkin.daily_claim_success'),
        serverity: 'success'
      })
    }
  }, [
    campaignId,
    dayInfo.day,
    dayInfo.cycle,
    setInfo,
    claimDailyCheckIn,
    fetchAfterOperation,
    setSnackInfo,
    t
  ])

  const handleOnClick = useCallback(async () => {
    if (R.isNotNil(campaignId)) {
      switch (cardState) {
        case DailyCheckInCardState.missed:
          setShowMakeupPopup(true)

          break

        case DailyCheckInCardState.checkIn:
          setInfo({
            message: t('reward.checkin.day_check_in'),
            confirm: t('common.confirm'),
            cancel: t('common.cancel'),
            onConfirm: handleOnCheckInConfirm
          })

          break

        case DailyCheckInCardState.claimable:
          handleOnClaimConfirm()

          break
      }
    }
  }, [
    campaignId,
    cardState,
    setShowMakeupPopup,
    setInfo,
    t,
    handleOnCheckInConfirm,
    handleOnClaimConfirm
  ])

  const disabled = useMemo(() => {
    const canCheckIn = cardState === DailyCheckInCardState.checkIn

    const canClaim = cardState === DailyCheckInCardState.claimable

    const canMakeup = cardState === DailyCheckInCardState.missed

    return !canCheckIn && !canClaim && !canMakeup
  }, [cardState])

  return (
    <CardButton
      className={className}
      disabled={disabled}
      onClick={handleOnClick}
    >
      <CardImage src={CheckInCardPNG.src} />
      <DayLabel>
        {t('adeptus.reward.checkin.day')}
        {dayInfo.day}
      </DayLabel>
      <QuantityLabel>+{dayInfo.rewards[0].quantity}</QuantityLabel>
      {renderOverlay(cardState)}
    </CardButton>
  )
}

type Props = {
  campaignId?: string
  className?: string
  dayInfo: DailyCheckInDay
}

export default CheckInDayCard
